import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as clientActions from '../../actions/clientActions';
import * as analyticsReportActions from '../../actions/analyticsReportActions';
import { withRouter } from '../../common/withRouter';
import AnalyticsReport from '../../components/analytics/AnalyticsReport';
import { MotifTabNavigation, MotifTabControl } from '@ey-xd/motif-react';
import { Link } from 'react-router';
/**
 * ExternalChecklistUserAnalyticsReportContainer container component
 * @extends {React.Component}
 */
class ExternalChecklistUserAnalyticsReportContainer extends React.Component {
  /**
   * Creates a new Analytics page
   * @constructor
   * @param {Object} props The component properties
   */
  constructor(props) {
    super(props);
    this.state = {
      selectedClientId: '',
      selectedReportId: '',
      selectedReitIds: [],
    };
    this.handleChangeClient = this.handleChangeClient.bind(this);
    this.handleChangeReport = this.handleChangeReport.bind(this);
    this.handleChangeReits = this.handleChangeReits.bind(this);
  }

  /**
   * Invoked immediately after mounting occurs
   * @returns {undefined}
   */
  componentDidMount() {
    this.props.clientActions.fetchAllActiveClients();
    this.props.analyticsReportActions.loadAnalyticsTaxClientDropdown();
  }

  /**
   * Invoked immediately after component updates
   * @returns {undefined}
   */
  componentDidUpdate(prevProps) {
    // Check if clients have been loaded
    if (
      this.props.externalChecklists.clients !==
      prevProps.externalChecklists.clients
    ) {
      const hasAnalyticsReport = this.props.externalChecklists.clients.some(
        (client) => !client.isAudit && client.isClientHasAnalyticsReport
      );

      // Redirect to PSQ if user doesn't have access to analytics
      if (
        this.props.externalChecklists.clients.length > 0 &&
        !hasAnalyticsReport
      ) {
        setTimeout(() => this.props.router.navigate('/ext/psq'), 0);
      }

      this.props.analyticsReportActions.loadAnalyticsTaxClientDropdown();
    }

    if (this.props.analyticsTaxClients !== prevProps.analyticsTaxClients) {
      if (
        this.props.analyticsTaxClients &&
        this.props.analyticsTaxClients.length === 1
      ) {
        this.handleChangeClient(this.props.analyticsTaxClients[0].clientID);
      }
    }
  }

  /**
   * Invoked immediately before unmounting occurs
   * @returns {undefined}
   */
  componentWillUnmount() {
    this.props.clientActions.fetchAllActiveClients(true);
  }

  handleChangeClient(clientId) {
    this.setState({
      selectedClientId: clientId,
      selectedReportId: '',
      selectedReitIds: [],
    });
    this.props.analyticsReportActions.loadAnalyticsReportDropdown(clientId);
    this.props.analyticsReportActions.loadAnalyticsREITDropdown(clientId);
  }

  handleChangeReport(reportId) {
    this.setState({ selectedReportId: reportId });
  }

  handleChangeReits(reitIds) {
    const normalizeIds = (val) => {
      if (Array.isArray(val)) {
        return val.map((v) =>
          v && typeof v === 'object' ? (v.value ?? v.key ?? v.reitID ?? v) : v
        );
      }
      if (val instanceof Set) {
        return Array.from(val).map((v) =>
          v && typeof v === 'object' ? (v.value ?? v.key ?? v.reitID ?? v) : v
        );
      }
      if (typeof val === 'string') {
        return [val];
      }
      if (val && typeof val === 'object' && Array.isArray(val.values)) {
        return val.values.map((v) =>
          v && typeof v === 'object' ? (v.value ?? v.key ?? v.reitID ?? v) : v
        );
      }
      return [];
    };
    const ids = normalizeIds(reitIds);
    this.setState({ selectedReitIds: ids });
    const { selectedClientId } = this.state;
    if (selectedClientId && ids.length > 0) {
      this.props.analyticsReportActions.loadAnalyticsReportPeriodsDropdown(
        selectedClientId,
        ids
      );
    }
  }

  /**
   * Render a React element
   * @returns {Object} A reference to the component
   */
  render() {
    let isAnalyticsFeatureEnabled =
      this.props.featureManagementData &&
      this.props.featureManagementData['isAnalyticsFeatureEnabled'];

    // Check if any client has analytics report enabled
    const hasAnalyticsReport = this.props.externalChecklists.clients.some(
      (client) => client.isClientHasAnalyticsReport === true
    );
    const { analyticsTaxClients } = this.props;
    const { selectedClientId, selectedReportId } = this.state;
    return (
      <React.Fragment>
        <MotifTabNavigation variant="default">
          <Link to={`/ext/psq`} tabIndex="-1">
            <MotifTabControl role="tab" data-testid="btnPSQ">
              PSQ
            </MotifTabControl>
          </Link>
          <Link to={`/ext/arqc`} tabIndex="-1">
            <MotifTabControl role="tab" data-testid="btnARQC">
              ARQC
            </MotifTabControl>
          </Link>
          {isAnalyticsFeatureEnabled && hasAnalyticsReport ? (
            <Link to={`/ext/analytics`} tabIndex="-1">
              <MotifTabControl
                className="motif-active"
                role="tab"
                data-testid="tbAnalytics"
              >
                Analytics
              </MotifTabControl>
            </Link>
          ) : (
            <></>
          )}
        </MotifTabNavigation>
        <div className="row mt-3">
          <div className="col">
            <h3 className="pageheader" data-testid="pghdrAnalyticsReports">
              Analytics Reports
            </h3>
          </div>
        </div>
        <AnalyticsReport
          clients={analyticsTaxClients}
          analyticsReports={this.props.analyticsReports}
          analyticsREITs={this.props.analyticsREITs}
          analyticsReportPeriods={this.props.analyticsReportPeriods}
          selectedClientId={selectedClientId}
          selectedReportId={selectedReportId}
          onChangeClient={this.handleChangeClient}
          onChangeReport={this.handleChangeReport}
          onChangeReits={this.handleChangeReits}
          initialValues={{
            clientName: selectedClientId,
            analyticsReport: '',
            reit: [],
            reportPeriod: [],
          }}
        />
      </React.Fragment>
    );
  }
}

/**
/**
 * Maps items from state to properties of the component
 * Maps items from state to properties of the component
 * @param {Object} state The state
 * @param {Object} state The state
 * @returns {Object} An object containing properties that the component can access
 * @returns {Object} An object containing properties that the component can access
 */
function mapStateToProps(state) {
  return {
    externalChecklists: {
      clients: state.externalChecklistsClients || [],
    },
    clients: state.externalChecklistsClients || [],
    analyticsReports: state.analyticsReports || [],
    analyticsTaxClients: state.analyticsTaxClients || [],
    analyticsREITs: state.analyticsREITs || [],
    analyticsReportPeriods: state.analyticsReportPeriods || [],
    featureManagementData: state.featureManagementData,
  };
}

/**
 * Binds actions to the dispatcher
 * @param {Object} dispatch The action dispatcher
 * @returns {Object} An object containing properties that the component can access
 */
function mapDispatchToProps(dispatch) {
  return {
    clientActions: bindActionCreators(clientActions, dispatch),
    analyticsReportActions: bindActionCreators(
      analyticsReportActions,
      dispatch
    ),
  };
}

ExternalChecklistUserAnalyticsReportContainer.propTypes = {
  clientActions: PropTypes.object,
  analyticsReportActions: PropTypes.object,
  externalChecklists: PropTypes.object,
  router: PropTypes.object,
  clients: PropTypes.array,
  analyticsTaxClients: PropTypes.array,
  analyticsReports: PropTypes.array,
  analyticsREITs: PropTypes.array,
  analyticsReportPeriods: PropTypes.array,
  featureManagementData: PropTypes.object,
};

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(ExternalChecklistUserAnalyticsReportContainer)
);
